import React, { useEffect, useState } from 'react';
import { Container, Stack, Divider } from '@fiverr-private/layout_components';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { logger } from '@fiverr-private/obs';
import { FederatedSeller, useSellerData } from '@fiverr-private/seller_pages_toolbox';
import { Highlights } from '../Highlights';
import { RolePageContactCard } from '../RolePageContactCard';
import { RolePageStickyFooter } from '../RolePageStickyFooter';
import { SaveToListButton } from '../SaveToListButton';
import { ShareButton } from '../ShareButton/ShareButton';
import { useContactSellerModalUpdater } from '../ContactSellerModal/hooks/useContactSellerModal';

const onRenderError = (originalError) => {
    const err = new Error('Failed while rendering the Sidebar Component');
    logger.error(err, {
        cause: originalError,
    });
};

export const Sidebar = ({ sidebarThresholdRef }: { sidebarThresholdRef?: React.RefObject<HTMLDivElement> }) => {
    const { sellerId, username } = useSellerData(selectSellerSlice);
    const [isInStickyMode, setIsInStickyMode] = useState(false);
    const setShowModal = useContactSellerModalUpdater();

    /*
     * Observe the sidebarThresholdRef to determine when the sidebar should switch to the sticky version
     */
    useEffect(() => {
        if (!sidebarThresholdRef || !sidebarThresholdRef.current) {
            return;
        }
        const observer = new IntersectionObserver(([bannerEl]) => {
            const { isIntersecting } = bannerEl;
            setIsInStickyMode(!isIntersecting);
        }, {});

        observer.observe(sidebarThresholdRef.current);
        return () => observer.disconnect();
    }, [sidebarThresholdRef]);

    const onContactOpen = () => {
        setShowModal(true);
    };

    return (
        <ErrorBoundary onError={onRenderError}>
            <Stack
                position="sticky"
                top="60px"
                background="white"
                boxShadow="z3"
                borderRadius="2xl"
                direction="column"
                gap="0"
                marginTop="6"
                hidden={{ default: true, lg: false }}
            >
                <RolePageContactCard withAvatar={isInStickyMode} onContactOpen={onContactOpen} />
                <Divider />
                <Stack paddingX="8" paddingTop="6" paddingBottom="8" gap="9" direction="column">
                    <Highlights />
                    <Stack justifyContent="spaceBetween">
                        <Container flex="1">
                            <SaveToListButton sellerId={sellerId} withLabel />
                        </Container>
                        <Container flex="1">
                            <ShareButton sellerId={sellerId} sellerName={username} withLabel />
                        </Container>
                    </Stack>
                </Stack>
            </Stack>
            <RolePageStickyFooter onContactOpen={onContactOpen} />
        </ErrorBoundary>
    );
};

const selectSellerSlice = (seller: FederatedSeller) => ({
    sellerId: seller.user.id,
    username: seller.user.name,
});
